import React, { useEffect, useRef, useState } from "react";
import { DISPLAY_OPTIONS, DisplayPrefs, loadDisplay, saveDisplay } from "../display";
import Icon from "./Icon";

// A small "Display" button that opens the comfort and accessibility switches.
export default function DisplaySettings() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<DisplayPrefs>(loadDisplay);
  const wrapRef = useRef<HTMLDivElement>(null);
  // The banner clips overflow, so the panel is placed on the screen under the button.
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const place = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const toggle = (key: keyof DisplayPrefs) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    saveDisplay(next);
  };

  return (
    <div ref={wrapRef}>
      <button
        type="button"
        className="btn btn-plain btn-sm"
        aria-expanded={open}
        aria-controls="display-settings"
        onClick={() => {
          place();
          setOpen((o) => !o);
        }}
      >
        <Icon name="eye" size={16} /> Display
      </button>
      {open && (
        <div
          id="display-settings"
          className="display-panel"
          role="group"
          aria-label="Display settings"
          style={{ top: pos.top, right: pos.right }}
        >
          <p className="font-display font-semibold">Display settings</p>
          <p className="mb-2 text-xs text-ink-soft">Saved on this device.</p>
          {DISPLAY_OPTIONS.map((o) => (
            <label key={o.key} className="check items-start py-1">
              <input
                type="checkbox"
                className="mt-1"
                checked={prefs[o.key]}
                onChange={() => toggle(o.key)}
              />
              <span>
                <span className="block text-sm font-bold">{o.label}</span>
                <span className="block text-xs text-ink-soft">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
